import { useEffect, useState } from "react";
import { MapPin, Plus, Check, Loader } from "lucide-react";
import { toast } from "react-toastify";
import { createAddress, listAddresses } from "./services";

/**
 * Orders are placed against a saved address id, so checkout needs a real
 * address book. Coordinates come from the location the app already detected
 * (the header's location picker writes it to localStorage); without one the
 * customer is asked to enable location before saving.
 */
const storedCoordinates = () => {
  try {
    const stored = JSON.parse(localStorage.getItem("coordinates"));
    const lat = Number(stored?.lat);
    const lon = Number(stored?.lon);
    if (Number.isFinite(lat) && Number.isFinite(lon)) return { lat, lon };
  } catch {
    /* nothing usable stored */
  }
  return null;
};

const storedAddressText = () => {
  try {
    return JSON.parse(localStorage.getItem("location"))?.address || "";
  } catch {
    return "";
  }
};

const emptyForm = {
  label: "Home",
  address: "",
  houseNumber: "",
  apartment: "",
  phone: "",
  deliveryInstructions: "",
};

export default function AddressPicker({ selectedId, onSelect }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      const list = await listAddresses();
      setAddresses(list);
      if (!selectedId && list.length) {
        onSelect((list.find((a) => a.isDefault) || list[0])._id);
      }
      if (!list.length) {
        setAdding(true);
        setForm((f) => ({ ...f, address: storedAddressText() }));
      }
    } catch (e) {
      toast.error(e?.message || "Could not load your saved addresses.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    const coords = storedCoordinates();
    if (!coords) {
      toast.warning(
        "Turn on location so we can pin your delivery address on the map.",
      );
      return;
    }
    try {
      setSaving(true);
      const created = await createAddress({
        ...form,
        latitude: coords.lat,
        longitude: coords.lon,
      });
      setAddresses((prev) => [...prev, created]);
      onSelect(created._id);
      setForm(emptyForm);
      setAdding(false);
      toast.success("Address saved.");
    } catch (err) {
      toast.error(err?.message || "Could not save the address.");
    } finally {
      setSaving(false);
    }
  };

  const field = (name, placeholder, extra = {}) => (
    <input
      value={form[name]}
      onChange={(e) => setForm((f) => ({ ...f, [name]: e.target.value }))}
      placeholder={placeholder}
      style={{
        width: "100%",
        padding: "9px 12px",
        borderRadius: 8,
        border: "1.5px solid #e5e7eb",
        fontSize: 13,
        fontFamily: "'Inter', sans-serif",
        background: "#f9fafb",
        outline: "none",
        boxSizing: "border-box",
      }}
      {...extra}
    />
  );

  return (
    <div className="co-card">
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 11,
          fontWeight: 700,
          color: "#9ca3af",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          fontFamily: "'Inter', sans-serif",
        }}
      >
        Delivery Address
      </p>

      {loading && (
        <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
          Loading your addresses…
        </p>
      )}

      {!loading &&
        addresses.map((address) => {
          const selected = address._id === selectedId;
          return (
            <label
              key={address._id}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                padding: "10px 12px",
                marginBottom: 8,
                borderRadius: 10,
                cursor: "pointer",
                border: selected ? "1.5px solid #0d6efd" : "1.5px solid #e5e7eb",
                background: selected ? "#eff6ff" : "#f9fafb",
              }}
            >
              <input
                type="radio"
                name="delivery-address"
                checked={selected}
                onChange={() => onSelect(address._id)}
                style={{ marginTop: 3, accentColor: "#0d6efd" }}
              />
              <div style={{ minWidth: 0 }}>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#111827",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  {address.label}
                  {address.isDefault && (
                    <span
                      style={{
                        marginLeft: 6,
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#059669",
                      }}
                    >
                      DEFAULT
                    </span>
                  )}
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: 12,
                    color: "#6b7280",
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  <MapPin size={11} style={{ marginRight: 4 }} />
                  {[address.houseNumber, address.apartment, address.address]
                    .filter(Boolean)
                    .join(", ")}
                </p>
              </div>
            </label>
          );
        })}

      {!loading && !adding && (
        <button
          type="button"
          onClick={() => {
            setForm((f) => ({ ...f, address: f.address || storedAddressText() }));
            setAdding(true);
          }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            padding: "8px 14px",
            borderRadius: 8,
            border: "1.5px solid #e5e7eb",
            background: "#fff",
            fontSize: 12,
            fontWeight: 600,
            color: "#374151",
            cursor: "pointer",
            fontFamily: "'Inter', sans-serif",
          }}
        >
          <Plus size={13} /> Add a new address
        </button>
      )}

      {adding && (
        <form
          onSubmit={handleSave}
          style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}
        >
          {field("label", "Label (Home, Office…)", { required: true, maxLength: 50 })}
          {field("address", "Full address", { required: true, maxLength: 300 })}
          <div style={{ display: "flex", gap: 8 }}>
            {field("houseNumber", "House / road")}
            {field("apartment", "Apartment / floor")}
          </div>
          {field("phone", "Contact phone (optional)")}
          {field("deliveryInstructions", "Delivery instructions (optional)")}

          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="submit"
              disabled={saving}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "9px 16px",
                borderRadius: 8,
                border: "none",
                background: saving ? "#9ca3af" : "#0d6efd",
                color: "#fff",
                fontSize: 13,
                fontWeight: 700,
                cursor: saving ? "not-allowed" : "pointer",
                fontFamily: "'Inter', sans-serif",
              }}
            >
              {saving ? <Loader size={13} /> : <Check size={13} />}
              {saving ? "Saving…" : "Save address"}
            </button>
            {addresses.length > 0 && (
              <button
                type="button"
                onClick={() => setAdding(false)}
                style={{
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "1.5px solid #e5e7eb",
                  background: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#6b7280",
                  cursor: "pointer",
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
